<script lang="ts">
 /**
 * Server-driven label format picker.
 *
 * The parent chooses which capability to expose and passes the filtered
 * formats. This component only groups and presents those choices.
 */
</script>

<script setup lang="ts">
import { computed } from "vue"

import { groupFormats, nextFormatIndex } from "@/lib/printing"
import type { PrintFormat } from "@/types/printing"

const props = defineProps<{ formats: PrintFormat[] }>()
const selected = defineModel<string>({ required: true })
const groups = computed(() => groupFormats(props.formats))

function caption(format: PrintFormat): string {
  const captions: Record<string, string> = {
    label: "QR + title",
    equipment: "Large QR + code",
    strip: "QR + text",
    square: "Die-cut",
    narrow: "QR + code",
  }

  return captions[format.key] ?? `${format.codes_per_label}-up`
}

function handleKeydown(event: KeyboardEvent) {
  const current = event.currentTarget as HTMLButtonElement
  const group = current.closest<HTMLElement>("[role='radiogroup']")
  const radios = [...(group?.querySelectorAll<HTMLButtonElement>("[role='radio']") ?? [])]
  const currentIndex = radios.indexOf(current)
  const nextIndex = nextFormatIndex(currentIndex, radios.length, event.key)
  if (nextIndex === currentIndex) return

  const target = radios[nextIndex]
  const key = target?.dataset.formatKey
  if (!target || !key) return

  event.preventDefault()
  selected.value = key
  target.focus()
}
</script>

<template>
  <div role="radiogroup" aria-label="Label format" class="flex flex-col gap-4">
    <section v-for="group in groups" :key="group.media" class="flex flex-col gap-2">
      <p class="text-muted text-xs font-medium tracking-wide uppercase">{{ group.media }}</p>

      <div class="grid grid-cols-2 gap-2 sm:grid-cols-3">
        <button
          v-for="format in group.formats"
          :key="format.key"
          type="button"
          role="radio"
          :data-format-key="format.key"
          :aria-checked="selected === format.key"
          :tabindex="selected === format.key ? 0 : -1"
          class="focus-visible:ring-primary cursor-pointer rounded-lg border-2 p-4 text-center transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
          :class="
            selected === format.key
              ? 'border-primary bg-primary/5'
              : 'border-default hover:border-accented'
          "
          @click="selected = format.key"
          @keydown="handleKeydown"
        >
          <div class="flex flex-col gap-2">
            <div class="flex h-8 items-center justify-center" aria-hidden="true">
              <div v-if="format.key === 'label'" class="flex items-center gap-1.5">
                <div class="bg-accented size-6 rounded-[2px]" />
                <div class="flex flex-col gap-1">
                  <div class="bg-accented/70 h-1.5 w-8 rounded-full" />
                  <div class="bg-accented/50 h-1.5 w-6 rounded-full" />
                </div>
              </div>

              <div v-else-if="format.key === 'equipment'" class="flex items-center gap-2">
                <div class="bg-accented size-7 rounded-[2px]" />
                <div class="bg-accented/70 h-2 w-9 rounded-full" />
              </div>

              <div v-else-if="format.key === 'standard'" class="flex gap-1">
                <div v-for="slot in 3" :key="slot" class="bg-accented size-5 rounded-[2px]" />
              </div>

              <div v-else-if="format.key === 'mini'" class="grid grid-cols-4 gap-0.5">
                <div v-for="slot in 8" :key="slot" class="bg-accented size-3 rounded-[1px]" />
              </div>

              <div v-else-if="format.key === 'strip'" class="flex items-center gap-1.5">
                <div class="bg-accented size-5 rounded-[1px]" />
                <div class="flex flex-col gap-0.5">
                  <div class="bg-accented/60 h-1 w-6 rounded-full" />
                  <div class="bg-accented/60 h-1 w-4 rounded-full" />
                </div>
              </div>

              <div v-else class="flex flex-col items-center gap-0.5">
                <div class="bg-accented size-5 rounded-[1px]" />
                <div class="bg-accented/60 h-0.5 w-4 rounded-full" />
              </div>
            </div>

            <div>
              <span class="text-highlighted block text-sm font-medium">{{
                format.display_name
              }}</span>
              <span class="text-muted mt-0.5 block text-xs">{{ caption(format) }}</span>
            </div>
          </div>
        </button>
      </div>
    </section>
  </div>
</template>
